<template>
  <div>
    <div
      v-if="displayLabel || hasTooltip"
      class="label-tooltip-wrapper"
    >
      <div
        v-if="displayLabel"
        id="textarea-field-label"
        :style="computedLabelStyle"
        :title="displayLabel"
      >
        {{ displayLabel }}
      </div>

      <v-tooltip
        v-if="hasTooltip"
        v-bind="tooltipProps"
      >
        <!-- Vue 3 + Vuetify 3 migration: change to v-slot:activator="{ props }" and v-bind="props" -->
        <template
          #activator="{ on, attrs }"
        >
          <v-icon
            class="ml-2"
            :style="computedTooltipIconStyle"
            v-bind="attrs"
            v-on="on"
          >
            {{ computedTooltipIcon }}
          </v-icon>
        </template>

        <template
          v-if="hasTooltipText"
        >
          <span>{{ computedTooltipText }}</span>
        </template>
        <template
          v-else-if="computedTooltipCustomComponent"
        >
          <component
            :is="computedTooltipCustomComponent"
            :config="computedTooltipConfig"
          />
        </template>
      </v-tooltip>
    </div>

    <v-textarea
      ref="textareaRef"
      v-model="computedTextValue"
      v-bind="computedProps"
      @input="onInput"
      @click="onClick"
      @click:append="onClickAppend"
      @click:append-inner="onClickAppendInner"
      @focusin="onFocusIn"
      @focusout="onFocusOut"
    >
      <template
        v-if="computedAppendInnerIcon"
        #append-inner
      >
        <v-icon>
          {{ computedAppendInnerIcon }}
        </v-icon>
      </template>
    </v-textarea>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount, defineExpose } from 'vue'
import {
  getBooleanFromValue,
  isArray,
  isBoolean,
  isEmpty,
  isFunction,
  isNumber,
  isObject,
  isSet,
  isString
} from '@/global/utils/utils'
import { useTranslate } from '@/global/composables/useTranslate'

const { translate } = useTranslate()

// --- Constants ---
const DEFAULT_ROWS = 5
const DEFAULT_TOOLTIP_COLOR = '#6B7280'
const DEFAULT_TOOLTIP_ICON = 'mdi-information-slab-circle'
const DEFAULT_TOOLTIP_FONT_SIZE = '16px'
const DEFAULT_TOOLTIP_MARGIN_BOTTOM = '1px'
const DEFAULT_ON_INPUT_TIMEOUT = 500

// --- Props & emits ---
const props = defineProps({
  field: { type: Object, default: () => ({}) },
  config: { type: Object, default: () => ({}) },
  value: { type: [Number, String, Object, Array], default: null },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  validationErrors: { type: Object, default: () => ({}) },
  rules: { type: Array, default: () => ([]) },
  collectionKey: { type: String, default: '' },
  collectionIndex: { type: Number, default: 0 },
  updateData: { type: Function, default: () => {} },
  updateValidationErrors: { type: Function, default: () => {} },
  formData: { type: Object, default: () => ({}) },
  autofocus: { type: Boolean, default: false },
  useFieldLabel: { type: Boolean, default: false },
  customFieldKey: { type: String, default: '' },
  removeDataPropertyByKey: { type: Function, default: () => {} },
  replacePropertyValueByKey: { type: Function, default: () => {} },
  fieldClass: { type: String, default: '' },
  editMode: { type: Boolean, default: false },
  createMode: { type: Boolean, default: false },
  // Fields are clearable unless a config/caller says otherwise.
  clearable: { type: Boolean, default: true }
})

const emit = defineEmits([
  'input',
  'click',
  'append-click',
  'append-inner-click',
  'clear-validation-errors',
  'focusin',
  'focusout',
  'typing'
])

// --- Template refs & local state ---
const textareaRef = ref(null)
const onInputTimeoutId = ref(null)

// --- Config resolution ---
const computedConfig = computed(() => {
  const isValid = (val) => isSet(val) && !isEmpty(val)
  if (isValid(props.field)) {
    return props.field
  }
  if (isValid(props.config)) {
    return props.config
  }
  return {}
})

const fieldProps = computed(() =>
  isSet(computedConfig.value?.props) && isObject(computedConfig.value.props)
    ? computedConfig.value.props
    : {}
)

// A field config can carry its rules either as the `rules` prop or inside its
// `props` pass-through - FieldsRenderer binds the latter, so both arrive.
const configuredRules = computed(() =>
  isArray(fieldProps.value?.rules) ? fieldProps.value.rules : props.rules
)

// --- Read / edit state ---
const dependantReadonly = computed(() =>
  isFunction(fieldProps.value?.dependantReadonly)
    ? fieldProps.value.dependantReadonly({ editState: props.editMode })
    : false
)

const isReadonly = computed(() =>
  props.readonly || !!computedConfig.value.readonly || dependantReadonly.value
)

const canEdit = computed(() => !isReadonly.value && !props.disabled)

const computedEditableProp = computed(() => {
  const { editable = true } = computedConfig.value || {}
  return isBoolean(editable) ? editable : false
})

// --- v-model ---
const computedTextValue = computed({
  get () {
    const { custom_display_value: customDisplayValue } = computedConfig.value?.options || {}
    return isFunction(customDisplayValue) ? customDisplayValue(props.value) : props.value
  },
  set (newVal) {
    emit('input', newVal)
  }
})

// --- Label ---
const label = computed(() => {
  const { label: cfgLabel } = computedConfig.value || {}
  return isString(cfgLabel) ? cfgLabel : ''
})

const displayLabel = computed(() => (props.useFieldLabel ? '' : label.value))
const displayFieldLabel = computed(() => (props.useFieldLabel ? label.value : ''))

// --- Validation ---
const computedRules = computed(() => {
  if (props.disabled || isReadonly.value) {
    return []
  }

  const ruleHandlers = {
    required: (value) => !!value || translate('base.this_field_mandatory')
  }

  return configuredRules.value.map((rule) =>
    isString(rule) && ruleHandlers[rule] ? ruleHandlers[rule] : rule
  )
})

const computedValidationErrors = computed(() => {
  let messages = []
  const { key, validationPropertyKey, validationErrorIncludeKey } = computedConfig.value || {}

  if (!key || isEmpty(props.validationErrors) || !canEdit.value) {
    return messages
  }

  for (const errorKey in props.validationErrors) {
    if (errorKey === key || errorKey === props.customFieldKey) {
      messages = props.validationErrors[errorKey]
    }
    else if (validationPropertyKey && errorKey === validationPropertyKey) {
      messages = props.validationErrors[errorKey]
    }
    else if (validationErrorIncludeKey && errorKey?.includes(validationErrorIncludeKey)) {
      messages = props.validationErrors[errorKey]
    }
    else {
      const collectionBase = `${props.collectionKey}.${props.collectionIndex}`
      if (errorKey === `${collectionBase}.${key}` || errorKey === collectionBase) {
        messages = props.validationErrors[errorKey]
      }
    }
  }

  return messages
})

// --- Styles & classes ---
const fieldStyle = computed(() => {
  const { style: configStyle } = computedConfig.value || {}
  // No grey background for non-editable fields (see TextField): that state is
  // carried by the text and cursor, not by the box.
  const base = {}
  return isSet(configStyle) && isObject(configStyle) ? { ...base, ...configStyle } : base
})

const computedFieldClasses = computed(() => {
  const { class: cfgClass, useDefaultEditStyle = true } = computedConfig.value || {}
  let classes = `mt-1 textarea${props.fieldClass ? ` ${props.fieldClass}` : ''}`

  if (props.editMode && canEdit.value && useDefaultEditStyle) {
    classes += ' textarea-field--is-editing'
  }
  if (!canEdit.value) {
    classes += ' textarea-field--readonly'
  }
  if (!computedEditableProp.value && !isReadonly.value) {
    classes += ' cursor-default'
  }
  if (computedValidationErrors.value.length) {
    classes += ' textarea-field--has-error'
  }
  if (cfgClass && isString(cfgClass)) {
    classes += ` ${cfgClass}`
  }

  return classes
})

const computedLabelStyle = computed(() => {
  const { labelStyle } = computedConfig.value || {}
  return isSet(labelStyle) && (isObject(labelStyle) || isString(labelStyle)) ? labelStyle : undefined
})

// --- Textarea-specific props ---
const computedRows = computed(() => {
  const { rows } = computedConfig.value || {}
  return isSet(rows) && isNumber(rows) ? rows : DEFAULT_ROWS
})

// --- Append / prepend ---
const computedAppendIcon = computed(() => {
  const { appendIcon } = computedConfig.value || {}
  return isString(appendIcon) ? appendIcon : ''
})

const computedPrependInnerIcon = computed(() => {
  const { prependInnerIcon } = computedConfig.value || {}
  return isString(prependInnerIcon) ? prependInnerIcon : undefined
})

const computedAppendInnerIcon = computed(() => {
  const { appendInnerIcon } = computedConfig.value || {}
  return isString(appendInnerIcon) ? appendInnerIcon : undefined
})

// --- Tooltip ---
const tooltip = computed(() => computedConfig.value?.tooltip)
const hasTooltip = computed(() => !!tooltip.value)
const computedTooltipText = computed(() => tooltip.value?.text)
const hasTooltipText = computed(() => !!computedTooltipText.value)
const computedTooltipIcon = computed(() => tooltip.value?.icon || DEFAULT_TOOLTIP_ICON)
const computedTooltipConfig = computed(() => tooltip.value?.config)
const computedTooltipCustomComponent = computed(() => tooltip.value?.component)

const computedTooltipIconStyle = computed(() => {
  const style = tooltip.value?.style
  return isObject(style)
    ? style
    : { color: DEFAULT_TOOLTIP_COLOR, fontSize: DEFAULT_TOOLTIP_FONT_SIZE, marginBottom: DEFAULT_TOOLTIP_MARGIN_BOTTOM }
})

const tooltipProps = computed(() => {
  const raw = isObject(tooltip.value) ? tooltip.value : {}
  const dir = tooltip.value?.direction
  return {
    top: !dir || dir === 'top',
    left: dir === 'left',
    right: dir === 'right',
    bottom: dir === 'bottom',
    ...raw
  }
})

// --- Misc field props ---
const computedPlaceholder = computed(() => {
  const { placeholder } = computedConfig.value || {}
  return isString(placeholder) && (props.editMode || props.createMode) ? placeholder : ''
})

const computedAutofocus = computed(() =>
  getBooleanFromValue(computedConfig.value?.autofocus) || props.autofocus
)

// --- All v-textarea props bundled ---
const computedProps = computed(() => ({
  class: computedFieldClasses.value,
  rows: computedRows.value,
  placeholder: computedPlaceholder.value,
  autofocus: computedAutofocus.value,
  dense: true,
  flat: true,
  outlined: true,
  label: displayFieldLabel.value,
  hideDetails: 'auto',
  readonly: isReadonly.value,
  disabled: props.disabled,
  errorMessages: computedValidationErrors.value,
  errorCount: computedValidationErrors.value.length || computedRules.value.length,
  // `??`, not `||`: now that the default is `true`, an explicit
  // `clearable: false` in the field config has to be able to win. Vuetify draws
  // the clear icon on readonly inputs too (and it stays clickable there), so the
  // field also has to actually be editable for it to make sense.
  clearable: (computedConfig.value?.clearable ?? props.clearable) && canEdit.value,
  prependInnerIcon: computedPrependInnerIcon.value,
  appendIcon: computedAppendIcon.value,
  style: fieldStyle.value,
  ...fieldProps.value,
  // After the spread on purpose - see TextField.vue for why the mapped
  // validators have to win over the raw shorthand strings in `props`.
  rules: computedRules.value
}))

// --- Shared callback context ---
function getFieldContext () {
  return {
    updateData: props.updateData,
    updateValidationErrors: props.updateValidationErrors,
    removeDataPropertyByKey: props.removeDataPropertyByKey,
    replacePropertyValueByKey: props.replacePropertyValueByKey,
    formData: props.formData,
    collectionKey: props.collectionKey,
    collectionIndex: props.collectionIndex
  }
}

// --- Watchers ---
watch(
  computedTextValue,
  (value) => {
    clearTimeout(onInputTimeoutId.value)
    const { onInput: cfgOnInput } = computedConfig.value || {}

    if (isFunction(cfgOnInput)) {
      onInputTimeoutId.value = setTimeout(() => {
        cfgOnInput(value, getFieldContext())
      }, DEFAULT_ON_INPUT_TIMEOUT)
    }
  }
)

onBeforeUnmount(() => clearTimeout(onInputTimeoutId.value))

// --- Event handlers ---
function onClick (event) {
  const { onClick: cfgOnClick } = computedConfig.value || {}
  if (isFunction(cfgOnClick)) {
    cfgOnClick({ event, editMode: props.editMode, createMode: props.createMode, formData: props.formData })
  }
  emit('click', event)
}

function onInput (val) {
  emit('clear-validation-errors')

  const payload = { value: val, ...getFieldContext() }
  emit('typing', payload)

  const { onTyping } = computedConfig.value || {}
  if (isFunction(onTyping)) {
    onTyping(payload)
  }
}

function onFocusIn (event) {
  const { onFocusIn: cfgOnFocusIn } = computedConfig.value || {}
  emit('focusin', { event })
  if (isFunction(cfgOnFocusIn)) {
    cfgOnFocusIn({ event, textValue: computedTextValue.value })
  }
}

function onFocusOut (event) {
  const { onFocusOut: cfgOnFocusOut } = computedConfig.value || {}
  emit('focusout', { event })
  if (isFunction(cfgOnFocusOut)) {
    cfgOnFocusOut({ event, textValue: computedTextValue.value })
  }
}

function onClickAppend () {
  const { appendIconClick } = computedConfig.value || {}
  emit('append-click')
  if (isFunction(appendIconClick)) {
    appendIconClick({
      ...getFieldContext(),
      editMode: props.editMode,
      createMode: props.createMode,
      value: computedTextValue.value
    })
  }
}

function onClickAppendInner () {
  emit('append-inner-click')
}

// --- Public API ---
function focus () {
  nextTick(() => {
    const el = textareaRef.value?.$el?.querySelector('textarea')
    if (el) {
      setTimeout(() => el.focus(), 100)
    }
  })
}

defineExpose({ focus })
</script>

<style lang="scss" scoped>
$textarea-font-family: 'Satoshi', sans-serif;
$textarea-font-size: 14px;
$textarea-line-height: 20px;
$textarea-color-default: #111827;
$textarea-color-muted: #6B7280;

::v-deep.textarea > .v-input__control > .v-input__slot > .v-text-field__slot > textarea {
  font-family: $textarea-font-family;
  font-size: $textarea-font-size !important;
  line-height: $textarea-line-height !important;
  color: $textarea-color-default !important;
  font-weight: 500 !important;
}

::v-deep {
  .v-input--is-disabled > .v-input__control > .v-input__slot > .v-text-field__slot > textarea {
    font-family: $textarea-font-family;
    font-size: $textarea-font-size !important;
    line-height: $textarea-line-height !important;
    color: $textarea-color-muted !important;
    font-weight: 400 !important;
  }

  .textarea-field--readonly .v-input__control > .v-input__slot > .v-text-field__slot > textarea {
    font-family: $textarea-font-family;
    font-size: $textarea-font-size !important;
    line-height: $textarea-line-height !important;
    color: $textarea-color-muted !important;
    font-weight: 400 !important;
  }

  .v-text-field--outlined fieldset {
    border-color: #E5E7EB !important;
    border-radius: 8px !important;
  }

  .textarea-field--is-editing fieldset {
    border-color: #65AD45 !important;
  }

  .cursor-default .v-input__control > .v-input__slot {
    cursor: default;
  }

  // Scoped to the input box itself (`.v-input__slot`), not the whole
  // `.v-input` - that also contains `.v-text-field__details` (the
  // validation message row below), which would otherwise get painted
  // green too and look like the field stretched down over the error text.
  .textarea-field--is-editing > .v-input__control > .v-input__slot {
    background-color: #F7FEE7 !important;
  }

  // Placed after the default/editing border rules above so it wins the
  // (equal-specificity) cascade regardless of edit state.
  .textarea-field--has-error fieldset {
    border-color: #DC2626 !important;
  }
}

#textarea-field-label {
  font-family: $textarea-font-family;
  font-size: $textarea-font-size;
  color: $textarea-color-default;
  line-height: $textarea-line-height;
  font-weight: 500;
  // Never wrap - see TextField. `min-width: 0` is needed because this is a flex
  // item inside `.label-tooltip-wrapper`.
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.label-tooltip-wrapper {
  display: flex;
}
</style>
